import React, { useEffect, useRef, useState } from 'lib/react';
import {
  EVGrid, EVList, EVListItem, EVTextField, EVPopper, EVPaper,
  EVClickAwayListener, EVKeyboardArrowDown, EVTypography, EVDateRangeIcon, EVKeyboardArrowUp,
  EVButton,
} from 'lib/components';
import clsx from 'clsx';
import MaskedDateInput from 'commonComponents/maskedDateInput';
import PropTypes from 'prop-types';
import {
  calculateDays, DATE_RANGES, getfilterOptions, getLast6MonthsRange, getLastMonthRange,
  getLastWeekRange, getLastYearRange, isValidCustomDate,
} from './DateFilter.utils';
import DateFilterStyles from './DateFilterStyles';

function DateRangeFilter(props) {
  const {
    startDate, endDate, dateError, selectedDateOption, handleDateRangeOptionChange, handleDateChange,
  } = props;
  const classes = DateFilterStyles();
  const filterOptions = getfilterOptions();
  const [anchorEl, setAnchorEl] = useState(null);
  const startDateInputRef = useRef(null);

  useEffect(() => {
    if (anchorEl) startDateInputRef.current.focus();
  }, [anchorEl]);

  const handleTextboxClick = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDropdownChange = (value) => {
    let dateValues;
    switch (value) {
      case DATE_RANGES.LAST_WEEK:
        dateValues = getLastWeekRange();
        break;
      case DATE_RANGES.LAST_MONTH:
        dateValues = getLastMonthRange();
        break;
      case DATE_RANGES.LAST_6_MONTHS:
        dateValues = getLast6MonthsRange();
        break;
      case DATE_RANGES.LAST_YEAR:
        dateValues = getLastYearRange();
        break;
      default:
        dateValues = [startDate, endDate];
        break;
    }
    handleDateRangeOptionChange(dateValues, value);
    setAnchorEl(anchorEl);
    startDateInputRef.current.focus();
  };

  return (
    <EVClickAwayListener onClickAway={handleClose}>
      <EVGrid
        className={classes.dateFilterContainer}
      >
        <EVButton
          id="date-range-filter-button"
          variant="outlined"
          data-testid="date-input-box"
          onClick={!anchorEl ? handleTextboxClick : undefined}
          className={clsx(classes.datePickerContainer, anchorEl && classes.datePickerPopperOpen)}
        >
          <EVDateRangeIcon htmlColor="#82899C" />
          {
          anchorEl
            ? (
              <>
                <EVTextField
                  inputRef={startDateInputRef}
                  id="startDate"
                  value={startDate}
                  onChange={handleDateChange}
                  placeholder="yyyy/mm/dd"
                  required
                  InputProps={{
                    inputComponent: MaskedDateInput,
                    classes: {
                      root: classes.inputTextfield,
                      underline: classes.inputTextfield,
                    },
                  }}
                  inputProps={{ maxLength: 10 }}
                >
                  <span className={classes.textPlaceHolder}>yyyy/mm/dd</span>
                </EVTextField>
                -
                <EVTextField
                  id="endDate"
                  required
                  value={endDate}
                  onChange={handleDateChange}
                  placeholder="yyyy/mm/dd"
                  InputProps={{
                    inputComponent: MaskedDateInput,
                    classes: {
                      root: classes.inputTextfield,
                      underline: classes.inputTextfield,
                    },
                  }}
                  inputProps={{ maxLength: 10 }}
                />
              </>
            )
            : (
              <EVTypography
                className={classes.optionValueTextField}
              >
                {filterOptions[selectedDateOption]?.description}
              </EVTypography>
            )
        }
          {anchorEl ? <EVKeyboardArrowUp onClick={handleClose} /> : <EVKeyboardArrowDown />}
        </EVButton>
        {(dateError) && (
        <EVTypography style={{ fontSize: '12px', color: '#BC1616' }}>
          {dateError}
        </EVTypography>
        )}
        <EVPopper
          id="menu-date"
          style={{ zIndex: 500, left: 0, right: 0 }}
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="bottom-start"
          disablePortal
        >
          <EVPaper style={{
            width: '100%',
            padding: '24px 24px',
            marginTop: dateError ? '20px' : '4px',
            overflow: 'hidden',
            overflowY: 'auto',
            minWidth: '200px',
          }}
          >
            <EVList style={{ padding: '0px' }}>
              {filterOptions.map((option) => (
                <EVListItem
                  id={`date-range-filter-list-item-${option.id}`}
                  tabIndex={option.id}
                  data-testid={`list-item-${option.id}`}
                  button
                  key={option.id}
                  className={clsx(classes.dateOptionList, selectedDateOption === option.id && classes.selectedOption)}
                  onClick={() => { handleDropdownChange(option.id); }}
                >
                  <EVTypography style={{
                    display: 'flex', alignSelf: 'end', fontSize: '16px',
                  }}
                  >
                    {option.description}
                  </EVTypography>
                  {option.id === DATE_RANGES.CUSTOM && isValidCustomDate(startDate, endDate) && (
                  <EVTypography
                    data-testid="custom-days-count"
                    className={classes.customDaysCountText}
                  >
                    {`${calculateDays(startDate, endDate)} days`}
                  </EVTypography>
                  )}
                </EVListItem>
              ))}
            </EVList>
          </EVPaper>
        </EVPopper>
      </EVGrid>
    </EVClickAwayListener>
  );
}

DateRangeFilter.propTypes = {
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  dateError: PropTypes.string.isRequired,
  selectedDateOption: PropTypes.number.isRequired,
  handleDateRangeOptionChange: PropTypes.func.isRequired,
  handleDateChange: PropTypes.func.isRequired,
};

export default DateRangeFilter;
